"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { Table, Select } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { getOtherProjectsDate } from "../../../redux/otherProjectsSlice/otherProjectsSlice";
import {
    getAllUnits,
    getExecutionReport,
    getOtherProjectsExecutionPlan,
} from "../../../redux/unitSlice/unitSlice";
import CustomSelectWithFilter from "../../../components/Common/CustomSelect/CustomSelectWithFilter";
import ActiveProject from "../../../assets/icons/Region.svg";

const { Option } = Select;

/* ===================== CONSTANTS ===================== */
const YEAR_TOTAL_SUFFIX = "__YEAR_TOTAL";

const headerStyle = {
    background: "#008dd3",
    color: "#fff",
    fontWeight: 600,
    textAlign: "center",
};

const MONTH_ORDER = [
    "Apr", "May", "Jun",
    "Jul", "Aug", "Sep",
    "Oct", "Nov", "Dec",
    "Jan", "Feb", "Mar",
];

/* ===================== UTILS ===================== */
const getFYShortLabel = (year) => {
    const y = Number(year);
    const next = y + 1;

    return `${String(y).slice(-2)}-${String(next).slice(-2)}`;
};

const getPreviousFYQ4Key = () => {
    const today = new Date();

    const currentFYStart =
        today.getMonth() >= 3
            ? today.getFullYear()
            : today.getFullYear() - 1;

    return `Q4-${currentFYStart - 1}`;
};

const getQ4Value = (periods) => {
    const q4Key = getPreviousFYQ4Key();

    if (periods[q4Key] !== undefined) {
        return toNumber(periods[q4Key]);
    }

    const fyYear = Number(q4Key.split("-")[1]) + 1;

    return (
        toNumber(periods[`Jan-${fyYear}`]) +
        toNumber(periods[`Feb-${fyYear}`]) +
        toNumber(periods[`Mar-${fyYear}`])
    );
};

const getBalanceTitle = () => {
    const today = new Date();
    const year = today.getFullYear() - 1; // previous year

    const shortYear = String(year).slice(-2);

    return `Order O/s as on 31-12-${shortYear}`;
};

const getAprilBalanceTitle = () => {
    const year = new Date().getFullYear(); // current year
    const shortYear = String(year).slice(-2);

    return `Balance as on 01-04-${shortYear}`;
};
/* ===================== BASE COLUMNS ===================== */

const baseColumn = {
    title: "Orders",
    dataIndex: "label",
    fixed: "left",
    width: 460,
    className: "orders-column",
    render: (text) => <strong>{text}</strong>,
    onHeaderCell: () => ({ style: headerStyle }),
};

const balanceRevenueColumn = {
    // title: "Order Outstanding as per Dec",
    title: getBalanceTitle(),
    dataIndex: "BALANCE_REVENUE",
    width: 120,
    align: "center",
    render: (val) => {
        // Handle "-" values from API
        if (val === 0 && val !== undefined) return "0";
        if (val === 0 || val === undefined || val === null) return "-";
        return formatNumber(val);
    },
    onHeaderCell: () => ({ style: headerStyle }),
};

const balanceRevenueDifferenceColumn = {
    // title: "Balance as on April",
    title: getAprilBalanceTitle(),
    dataIndex: "BALANCE_REVENUE_DIFF",
    width: 120,
    align: "center",
    render: (val) => {
        if (val === 0 || val === undefined || val === null) return "-";
        return formatNumber(val);
    },
    onHeaderCell: () => ({ style: headerStyle }),
};

const overallColumn = {
    title: "Overall Total",
    dataIndex: "ROW_OVERALL_TOTAL",
    fixed: "right",
    width: 100,
    align: "center",
    className: "total-column",
    render: (val) => formatNumber(val),
    onHeaderCell: () => ({ style: headerStyle }),
};



const buildYearTotalColumn = (year) => ({
    title: "Total",
    dataIndex: `${year}${YEAR_TOTAL_SUFFIX}`,
    width: 140,
    align: "center",
    render: (val) => formatNumber(val),
    onHeaderCell: () => ({
        style: {
            background: "#006da4",
            color: "#fff",
            fontWeight: 600,
        },
    }),
});

/* ===================== NORMALIZE ===================== */
/* ----------------- CONVERT VALUE TO NUMBER ----------------- */
export const toNumber = (v) =>
    v ? Number(String(v).replace(/,/g, "")) || 0 : 0;
// const toNumber = (v) => {
//   const n = Number(v);
//   return isNaN(n) ? 0 : n;
// };

const isMonthKey = (key) =>
    /^[A-Z][a-z]{2}-\d{4}$/.test(key) ||   // Apr-2026
    /^[A-Z][a-z]{2}_\d{2}$/.test(key);     // Apr_26


const isQuarterKey = (key) =>
    /^Q[1-4]-\d{4}$/.test(key) ||        // legacy: Q1-2026
    /^Q[1-4]-\d{4}-\d{4}$/.test(key);

const isYearKey = (key) =>
    /^\d{4}-\d{4}$/.test(key); // 2025-2026


export const getYearFromKey = (key, viewType) => {
    if (/^\d{4}-\d{4}$/.test(key)) {
        return viewType === "Year" ? key : key.split("-")[0];
    }

    // NEW: Q1-2026-2027 → year group is "2026-2027"
    if (/^Q[1-4]-\d{4}-\d{4}$/.test(key)) {
        return `${key.split("-")[1]}-${key.split("-")[2]}`;
    }

    // legacy quarter: Q1-2026 → "2026"
    if (/^Q[1-4]-\d{4}$/.test(key)) {
        return key.split("-")[1];
    }

    if (/^[A-Za-z]{3}-\d{4}$/.test(key)) {
        const [month, year] = key.split("-");
        const y = Number(year);
        if (["Jan", "Feb", "Mar"].includes(month)) {
            return `${y - 1}-${y}`;
        }
        return `${y}-${y + 1}`;
    }

    if (/^[A-Za-z]{3}_\d{2}$/.test(key)) {
        const shortYear = Number(key.split("_")[1]);
        return `20${shortYear}`;
    }

    return null;
};

/* ----------------- FORMAT NUMBER ----------------- */
const nf2 = new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
});
const headerFYLabel = (yearKey) => {
    // Month grouping gives "2024", "2025" etc → show "2024-2025"
    if (/^\d{4}$/.test(yearKey)) {
        const y = Number(yearKey);
        return `${y}-${y + 1}`;
    }

    // Quarter/Year already gives "2024-2025" → keep as-is
    if (/^\d{4}-\d{4}$/.test(yearKey)) return yearKey;

    return yearKey;
};

export const formatNumber = (val) => {
    const num = toNumber(val);
    // keep "-" for truly missing/undefined/null; show 0.00 for 0
    if (val === undefined || val === null || val === "") return "-";
    if (num === 0) return "0";
    return nf2.format(num);
};

export const normalizePeriodsOnly = (data = {}, viewType, isAnticipated = false) => {
    const result = {};

    Object.entries(data)?.forEach(([key, value]) => {
        let normalizedKey = key;

        if (isAnticipated) {
            if (/^[A-Za-z]{3}_\d{2}$/.test(key)) {
                const [month, yy] = key.split("_");
                normalizedKey = `${month}-20${yy}`;
            }
        }

        const num = toNumber(value);
        if (Number.isNaN(num) || num === undefined || num === null) return;

        // MONTH -> QUARTER CONVERSION
        // Only derive a quarter from months when the source has no literal quarter
        // keys of its own (anticipated_orders). Clear/hold/other-projects data
        // already carries an authoritative "Q1-2026"-style key alongside its month
        // keys — deriving one here too would make both versions collide on the
        // same result key and overwrite each other in object-iteration order.
        if (isAnticipated && viewType === "Quarter" && isMonthKey(normalizedKey)) {
            const [month, yearStr] = normalizedKey.split("-");
            const year = Number(yearStr);

            let quarter = "";
            let fyStartYear;

            if (["Apr", "May", "Jun"].includes(month)) { quarter = "Q1"; fyStartYear = year; }
            else if (["Jul", "Aug", "Sep"].includes(month)) { quarter = "Q2"; fyStartYear = year; }
            else if (["Oct", "Nov", "Dec"].includes(month)) { quarter = "Q3"; fyStartYear = year; }
            else if (["Jan", "Feb", "Mar"].includes(month)) { quarter = "Q4"; fyStartYear = year - 1; }

            normalizedKey = `${quarter}-${fyStartYear}`;
            // Q4 of FY2026-2027: Jan/Feb/Mar-2027 → Q4-2026 ✅
        }

        // APPLY FILTERS
        if (viewType === "Month" && !isMonthKey(normalizedKey)) return;
        if (viewType === "Quarter" && !isQuarterKey(normalizedKey)) return;
        if (viewType === "Year" && !isYearKey(normalizedKey)) return;

        // result[normalizedKey] = (result[normalizedKey] || 0) + num;
        result[normalizedKey] = num;
    });

    return result;
};


const normalizeData = (data, viewType, isAnticipated = false) => {
    const result = {};
    const yearTotals = {};
    if (!data) return result;

    Object.entries(data).forEach(([key, value]) => {
        let normalizedKey = key;

        // Convert Apr_26 → Apr-2026 (FY-aware)
        if (/^[A-Za-z]{3}_\d{2}$/.test(key)) {
            const [month, yy] = key.split("_");
            // Do NOT adjust year here — keep as calendar year
            // Jan_27 → Jan-2027, Mar_27 → Mar-2027
            normalizedKey = `${month}-20${yy}`;
        }

        if (
            ["PROJ_NAME", "IO_NO", "REV_NO", "description", "upto", "Total"].includes(key)
        ) return;

        const num = toNumber(value);
        if (num === undefined || num === null) return;

        // MONTH -> QUARTER CONVERSION for anticipated
        // if (viewType === "Quarter" && isMonthKey(normalizedKey)) {
        if (
            isAnticipated &&
            viewType === "Quarter" &&
            isMonthKey(normalizedKey)
        ) {
            const [month, yearStr] = normalizedKey.split("-");
            const year = Number(yearStr);

            let quarter = "";
            let fyStartYear;

            if (["Apr", "May", "Jun"].includes(month)) { quarter = "Q1"; fyStartYear = year; }
            else if (["Jul", "Aug", "Sep"].includes(month)) { quarter = "Q2"; fyStartYear = year; }
            else if (["Oct", "Nov", "Dec"].includes(month)) { quarter = "Q3"; fyStartYear = year; }
            else if (["Jan", "Feb", "Mar"].includes(month)) { quarter = "Q4"; fyStartYear = year - 1; }

            normalizedKey = `${quarter}-${fyStartYear}`;
        }

        if (viewType === "Month" && !isMonthKey(normalizedKey)) return;
        if (viewType === "Quarter" && !isQuarterKey(normalizedKey)) return;
        if (viewType === "Year" && !isYearKey(normalizedKey)) return;

        result[normalizedKey] = (result[normalizedKey] || 0) + num;

        if (viewType === "Quarter") {
            yearTotals[normalizedKey.split("-")[1]] =
                (yearTotals[normalizedKey.split("-")[1]] || 0) + num;
        }
        if (viewType === "Month") {
            const fyYear = getYearFromKey(normalizedKey, viewType);
            yearTotals[fyYear] = (yearTotals[fyYear] || 0) + num;
        }
    });

    result.ROW_OVERALL_TOTAL = Object.values(result)
        .filter((_, i) => !Object.keys(result)[i]?.endsWith("__YEAR_TOTAL"))
        .reduce((a, b) => a + b, 0);

    Object.entries(yearTotals).forEach(([y, v]) => {
        result[`${y}${YEAR_TOTAL_SUFFIX}`] =
            viewType === "Year" && data?.TOTAL_SUM != null
                ? toNumber(data.TOTAL_SUM)
                : v;
    });

    return result;
};

const normalizeData1 = (data, viewType) => {
    const result = {};
    const yearTotals = {};
    if (!data) return result;

    Object.entries(data).forEach(([key, value]) => {
        let normalizedKey = key;

        // Convert Apr_26 → Apr-2026
        if (/^[A-Za-z]{3}_\d{2}$/.test(key)) {
            const [month, yy] = key.split("_");
            normalizedKey = `${month}-20${yy}`;
        }
        // skip meta & non-numeric
        if (
            [
                "PROJ_NAME",
                "IO_NO",
                "REV_NO",
                "description",
                "upto",
                "Total",
            ].includes(key)
        ) {
            return;
        }
        if (viewType === "Month" && !isMonthKey(normalizedKey)) return;
        if (viewType === "Quarter" && !isQuarterKey(normalizedKey)) return;
        if (viewType === "Year" && !isYearKey(normalizedKey)) return;
        const num = toNumber(value);
        if (num === undefined || num === null) return;

        // ---------------- VIEW TYPE FILTER ----------------
        const year = getYearFromKey(normalizedKey, viewType);
        // normal cell
        result[normalizedKey] = num;

        yearTotals[year] = (yearTotals[year] || 0) + num;
        // console.log("year", year);

    });
    // console.log("year",yearTotals,result);

    result.ROW_OVERALL_TOTAL = Object.entries(yearTotals)
        .filter(([key]) => key !== null && key !== "null")
        .reduce((sum, [, value]) => sum + value, 0);
    // ---------------- OVERALL TOTAL ----------------
    // result.ROW_OVERALL_TOTAL =
    //   data?.Total != null ? toNumber(data.Total) : calculatedOverall;
    Object.entries(yearTotals).forEach(([y, v]) => {
        result[`${y}${YEAR_TOTAL_SUFFIX}`] =
            viewType === "Year" && data?.TOTAL_SUM != null
                ? toNumber(data.TOTAL_SUM)
                : v;
    });
    return result;
};

export const buildProjectRow = (project, viewType) => {
    const norm = normalizeData(project, viewType);
    const row = {
        key: `PROJ_${project.IO_NO}`,
        label: project.PROJ_NAME,
        BALANCE_REVENUE: project.BALANCE_REVENUE === "-" ? "-" : toNumber(project.BALANCE_REVENUE),
    };
    Object.entries(norm)?.forEach(([k, v]) => {
        row[k] = v;
    });
    return row;
};

/* ===================== COMPONENT ===================== */

const ExecutionPlan = () => {
    const dispatch = useDispatch();
    // `state.unit.loading` is shared across many unrelated thunks throughout
    // the app, so it can flip back to false the moment ANY of them resolves
    // - not specifically the fetch this table is waiting on. Track loading
    // locally instead, scoped to just this page's own fetch (mirrors
    // ExecutionPlanReport/index.jsx).
    const [loading, setLoading] = useState(false);
    const requestIdRef = useRef(0);

    const { otherProjectsExecutionPlan, allUnitsList } = useSelector((state) => state.unit);
    const { user } = useSelector((state) => state.auth);
    const { systemDate } = useSelector((state) => state.otherProjects);
    const restrictedUser = ["unit", "region"].includes(user?.role?.toLowerCase());

    const [searchParams] = useSearchParams();
    const defaultUnit = searchParams.get("unit");
    const defaultViewType = searchParams.get("viewType");
    const defaultParameter = searchParams.get("parameter");
    const isClearParam = searchParams.get("IS_CLEAR");

    const [viewType, setViewType] = useState(defaultViewType || "Quarter");
    const [unit, setUnit] = useState(defaultUnit || "BHEL");
    const [parameter, setParameter] = useState(defaultParameter || "PROJECT_COST");

    // Label shown in the "No data found" message — matches the Unit
    // dropdown's own labels below.
    const selectedUnitLabel = useMemo(() => {
        if (unit === "BHEL") return "BHEL Overall";
        return (allUnitsList || []).find((u) => u.MNF_UNIT === unit)?.UNIT_DISPLAY_NAME || unit;
    }, [unit, allUnitsList]);

    const isBhel = unit === "BHEL";
    //  ADD THESE HELPERS (just below MONTH_ORDER or near utils)
    const fySort = (a, b) => Number(a.split("-")[0]) - Number(b.split("-")[0]);

    // const getQuarterLabel = (key) => key.split("-")[0]; // Q1-2025 => Q1
    const getQuarterLabel = (key) => key.split("-")[0];
    const getCurrentFYStartYear = () => {
        const today = new Date();

        return today.getMonth() >= 3
            ? today.getFullYear()
            : today.getFullYear() - 1;
    };
    useEffect(() => {
        dispatch(getOtherProjectsDate("system"));
    }, [dispatch]);

    useEffect(() => {
        if (restrictedUser) {
            setUnit(sessionStorage.getItem("user_id"));
        }
    }, [restrictedUser]);
    useEffect(() => {
        const currentRequestId = ++requestIdRef.current;

        setLoading(true);
        dispatch(
            getOtherProjectsExecutionPlan({
                FILTER_PARAMETER: parameter,

                MNF_UNIT: restrictedUser
                    ? sessionStorage.getItem("user_id")
                    : isBhel
                        ? null
                        : unit,

                IS_BHEL_OVERALL: restrictedUser ? false : isBhel,

                ...(isClearParam !== null ? { IS_CLEAR: isClearParam } : {}),

                notifyWithFilter: true,
            })
        ).finally(() => {
            if (currentRequestId === requestIdRef.current) {
                setLoading(false);
            }
        });
        dispatch(getAllUnits());
    }, [dispatch, parameter, unit, isBhel, isClearParam]);

    /* ---------- DATA ---------- */
    const buildBhelSummaryRow = (label, units, key, viewType) => {
        const combined = {};
        let totalBalanceRevenue = 0;

        units?.forEach((u) => {
            const data = u[key];
            if (!data) return;

            const norm = normalizeData1(data, viewType); // normalize EACH unit
            // console.log("data", data);

            // Add balance revenue from this unit's data
            if (data.BALANCE_REVENUE && data.BALANCE_REVENUE !== "-") {
                totalBalanceRevenue += toNumber(data.BALANCE_REVENUE);
            }

            Object.entries(norm).forEach(([k, v]) => {
                //  skip derived keys
                if (k.endsWith("__YEAR_TOTAL")) return;
                if (k === "ROW_OVERALL_TOTAL") return;

                combined[k] = (combined[k] || 0) + v;
            });
        });

        // normalize ONCE at the end
        const finalNorm = normalizeData1(combined, viewType);

        const row = {
            key: label,
            label,
            isSubtotal: true,
            BALANCE_REVENUE: totalBalanceRevenue
        };

        Object.entries(finalNorm)?.forEach(([k, v]) => {
            if (k !== "BALANCE_REVENUE") {
                row[k] = formatNumber(v);
            }
        });

        return row;
    };

    const buildGrandTotalRow = (rows) => {
        const combined = {};

        rows.forEach((row) => {
            Object.entries(row).forEach(([key, value]) => {

                if (
                    key === "key" ||
                    key === "label" ||
                    key === "children" ||
                    key === "projectMap" ||
                    key === "isSubtotal" ||
                    key === "IO_NO"
                    ||

                    // EXCLUDE STATIC REVENUE COLS
                    key === "BALANCE_REVENUE" ||
                    key === "BALANCE_REVENUE_DIFF"
                ) {
                    return;
                }

                combined[key] =
                    (combined[key] || 0) +
                    toNumber(value);
            });
        });

        return {
            key: "GRAND_TOTAL",
            label: "Grand Total",
            isGrandTotal: true,

            ...Object.fromEntries(
                Object.entries(combined).map(([k, v]) => [
                    k,
                    formatNumber(v),
                ])
            ),
        };
    };

    const firstQuarter =
        viewType === "Quarter"
            ? getPreviousFYQ4Key()
            : null;

    const tableData = useMemo(() => {
        if (!otherProjectsExecutionPlan?.units) return [];

        const sectorMap = {};

        otherProjectsExecutionPlan.units.forEach((unit) => {
            (unit.other_projects || []).forEach((sector) => {
                (sector.data || []).forEach((project) => {
                    console.log("PROJECT KEYS:", Object.keys(project));
                    console.log("PROJECT SAMPLE:", project);
                });
            });
        });
        otherProjectsExecutionPlan.units.forEach((unit) => {
            (unit.other_projects || []).forEach((sector) => {
                const sectorName = sector.BS_UNIT_NAME;

                if (!sectorMap[sectorName]) {
                    sectorMap[sectorName] = {
                        key: sectorName,
                        label: sectorName,
                        total: 0,
                        projectMap: {},
                        _sectorBalanceRevenue: 0,
                        _rawPeriods: {}, // ✅ for sector-level Q4 aggregation
                    };
                }

                (sector.data || []).forEach((project) => {
                    // Group by IO_NO, not PROJ_NAME — multiple distinct orders
                    // frequently share the same project name (revisions/splits),
                    // and merging by name alone summed unrelated orders into one
                    // row (a genuinely all-zero order would inherit a same-named
                    // sibling order's nonzero totals).
                    const projectKey = project.IO_NO;

                    if (!sectorMap[sectorName].projectMap[projectKey]) {
                        sectorMap[sectorName].projectMap[projectKey] = {
                            key: `${sectorName}_${project.IO_NO}`,
                            label: project.PROJ_NAME,
                            IO_NO: project.IO_NO,
                            IS_INDAS: project.IS_INDAS,
                            total: 0,
                            _rawPeriods: {}, // ✅ always Quarter-normalized for Q4 lookup
                            BALANCE_REVENUE: 0,
                        };
                    }

                    sectorMap[sectorName].projectMap[projectKey].BALANCE_REVENUE +=
                        toNumber(project.BALANCE_REVENUE || 0);
                    // ✅ Accumulate BALANCE_REVENUE at sector level
                    sectorMap[sectorName]._sectorBalanceRevenue +=
                        toNumber(project.BALANCE_REVENUE || 0);

                    // ✅ Raw periods (always Quarter) for Q4 lookup
                    const rawPeriods = normalizePeriodsOnly(project, "Quarter");
                    Object.entries(rawPeriods).forEach(([k, v]) => {
                        sectorMap[sectorName].projectMap[projectKey]._rawPeriods[k] =
                            (sectorMap[sectorName].projectMap[projectKey]._rawPeriods[k] || 0) + v;
                        sectorMap[sectorName]._rawPeriods[k] =
                            (sectorMap[sectorName]._rawPeriods[k] || 0) + v;
                    });

                    // Project total
                    sectorMap[sectorName].projectMap[projectKey].total =
                        toNumber(sectorMap[sectorName].projectMap[projectKey].total) +
                        toNumber(project.total || 0);

                    // Period keys for display
                    Object.keys(project).forEach((key) => {
                        const isYear = /^\d{4}-\d{4}$/.test(key);
                        const isQuarter = /^Q[1-4]-\d{4}$/.test(key);
                        const isMonth =
                            /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)-\d{4}$/.test(key);

                        const shouldInclude =
                            (viewType === "Year" && isYear) ||
                            (viewType === "Quarter" && isQuarter) ||
                            (viewType === "Month" && isMonth);

                        if (!shouldInclude) return;

                        // ✅ Exclude past FY data
                        const year = getYearFromKey(key, viewType);

                        if (year) {
                            const yearStart =
                                /^\d{4}-\d{4}$/.test(year)
                                    ? Number(year.split("-")[0])
                                    : Number(year);

                            if (yearStart < getCurrentFYStartYear()) {
                                return;
                            }
                        }

                        const value = toNumber(project[key] || 0);
                        sectorMap[sectorName].projectMap[projectKey][key] =
                            toNumber(sectorMap[sectorName].projectMap[projectKey][key]) + value;

                        sectorMap[sectorName][key] =
                            toNumber(sectorMap[sectorName][key]) + value;
                    });
                });
            });
        });

        const sectorRows = Object.values(sectorMap).map((sector) => {
            // ✅ Sector Q4 value from raw periods
            const sectorQ4Value = getQ4Value(sector._rawPeriods);
            const sectorBalanceRevenue = sector._sectorBalanceRevenue;

            const children = Object.values(sector.projectMap).map((project) => {
                const q4Value = getQ4Value(project._rawPeriods);
                const balanceRevenue = toNumber(project.BALANCE_REVENUE || 0);

                const projectYearTotals = {};

                Object.keys(project).forEach((key) => {
                    if (
                        viewType === "Quarter" &&
                        /^Q[1-4]-\d{4}$/.test(key)
                    ) {
                        const year = key.split("-")[1];

                        projectYearTotals[`${year}${YEAR_TOTAL_SUFFIX}`] =
                            (projectYearTotals[`${year}${YEAR_TOTAL_SUFFIX}`] || 0) +
                            toNumber(project[key]);
                    }

                    if (
                        viewType === "Month" &&
                        /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)-\d{4}$/.test(key)
                    ) {
                        const year = getYearFromKey(key, viewType);

                        projectYearTotals[`${year}${YEAR_TOTAL_SUFFIX}`] =
                            (projectYearTotals[`${year}${YEAR_TOTAL_SUFFIX}`] || 0) +
                            toNumber(project[key]);
                    }

                    if (
                        viewType === "Year" &&
                        /^\d{4}-\d{4}$/.test(key)
                    ) {
                        projectYearTotals[`${key}${YEAR_TOTAL_SUFFIX}`] =
                            toNumber(project[key]);
                    }
                });

                return {
                    ...project,
                    ...projectYearTotals,

                    [firstQuarter]: q4Value,
                    PREVIOUS_Q4_VALUE: q4Value,
                    BALANCE_REVENUE: balanceRevenue,

                    BALANCE_REVENUE_DIFF:
                        balanceRevenue - q4Value,

                    // Use API project total directly
                    // total: formatNumber(project.total),
                    total: formatNumber(
                        Object.entries(project)
                            .filter(([key]) => {
                                return (
                                    (viewType === "Year" &&
                                        /^\d{4}-\d{4}$/.test(key)) ||

                                    (viewType === "Quarter" &&
                                        /^Q[1-4]-\d{4}$/.test(key)) ||

                                    (viewType === "Month" &&
                                        /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)-\d{4}$/.test(key))
                                );
                            })
                            .reduce((sum, [, value]) => sum + toNumber(value), 0)
                    ),

                    //  Format only FY keys
                    ...Object.fromEntries(
                        Object.entries(project)
                            .filter(([key]) =>
                                /^\d{4}-\d{4}$/.test(key)
                            )
                            .map(([key, value]) => [
                                key,
                                formatNumber(value),
                            ])
                    ),
                };
            });
            const calculatedSectorTotal = children.reduce(
                (sum, child) => sum + toNumber(child.total),
                0
            );
            return {
                ...sector,
                [firstQuarter]: sectorQ4Value,
                PREVIOUS_Q4_VALUE: sectorQ4Value,
                BALANCE_REVENUE: sectorBalanceRevenue,
                BALANCE_REVENUE_DIFF: sectorBalanceRevenue - sectorQ4Value, // ✅
                children,
                // total: Number(sector.total).toLocaleString("en-IN"),
                total: formatNumber(calculatedSectorTotal),
                // Year totals for quarter/month grouped headers
                ...Object.keys(sector).reduce((acc, key) => {
                    if (viewType === "Quarter" && /^Q[1-4]-\d{4}$/.test(key)) {
                        const year = key.split("-")[1];
                        acc[`${year}${YEAR_TOTAL_SUFFIX}`] =
                            (acc[`${year}${YEAR_TOTAL_SUFFIX}`] || 0) + toNumber(sector[key]);
                    }
                    if (viewType === "Month" &&
                        /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)-\d{4}$/.test(key)) {
                        const year = getYearFromKey(key, viewType);
                        acc[`${year}${YEAR_TOTAL_SUFFIX}`] =
                            (acc[`${year}${YEAR_TOTAL_SUFFIX}`] || 0) + toNumber(sector[key]);
                    }
                    return acc;
                }, {}),
            };
        });

        // ✅ Grand total aggregates BALANCE_REVENUE and BALANCE_REVENUE_DIFF from sectors
        const grandTotalRow = buildGrandTotalRow(sectorRows);
        const grandBalanceRevenue = sectorRows.reduce(
            (sum, r) => sum + toNumber(r.BALANCE_REVENUE), 0
        );
        const grandBalanceDiff = sectorRows.reduce(
            (sum, r) => sum + toNumber(r.BALANCE_REVENUE_DIFF), 0
        );

        grandTotalRow.BALANCE_REVENUE = grandBalanceRevenue;
        grandTotalRow.BALANCE_REVENUE_DIFF = grandBalanceDiff;

        return [grandTotalRow, ...sectorRows];
    }, [otherProjectsExecutionPlan, viewType]);

    /* ---------- FLATTEN ---------- */

    const rows = useMemo(() => {
        const flat = [];
        const walk = (r) => {
            flat.push(r);
            r.children?.forEach(walk);
        };
        tableData?.forEach(walk);
        return flat;
    }, [tableData]);

    /* ---------- EXPAND ALL / COLLAPSE ALL ---------- */

    // Only rows carrying a non-empty `children` array (the sector rows) can
    // ever be expanded — project rows are leaves.
    const expandableRowKeys = useMemo(
        () => rows.filter((row) => Array.isArray(row.children) && row.children.length > 0).map((row) => row.key),
        [rows]
    );

    const [expandedRowKeys, setExpandedRowKeys] = useState([]);

    const isAllExpanded =
        expandableRowKeys.length > 0 &&
        expandableRowKeys.every((key) => expandedRowKeys.includes(key));

    const handleToggleExpandAll = () => {
        setExpandedRowKeys(isAllExpanded ? [] : expandableRowKeys);
    };

    const groupedKeys = useMemo(() => {
        const map = {};
        const prevQ4Key = getPreviousFYQ4Key(); // Q4-2025
        const currentFYStartYear = getCurrentFYStartYear(); // 2026

        if (!rows || !Array.isArray(rows)) return map;

        rows.forEach((r) => {
            if (!r || typeof r !== "object") return;
            Object.keys(r).forEach((k) => {
                const valid =
                    (viewType === "Year" && /^\d{4}-\d{4}$/.test(k)) ||
                    (viewType === "Quarter" && /^Q[1-4]-\d{4}$/.test(k)) ||
                    (viewType === "Month" &&
                        /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)-\d{4}$/.test(k));

                if (!valid) return;
                if (k === prevQ4Key) return; // standalone col, not in group

                const year = getYearFromKey(k, viewType);
                if (!year) return;

                const yearStart = /^\d{4}-\d{4}$/.test(year)
                    ? Number(year.split("-")[0])
                    : Number(year);

                // ✅ Only current FY
                if (yearStart < currentFYStartYear) return;
                // if (yearStart > currentFYStartYear) return;

                if (!map[year]) map[year] = new Set();
                map[year].add(k);
            });
        });

        Object.keys(map).forEach((y) => {
            if (!map[y]) return;
            map[y] = Array.from(map[y]).sort((a, b) => {
                if (viewType === "Month") {
                    return MONTH_ORDER.indexOf(a.split("-")[0]) - MONTH_ORDER.indexOf(b.split("-")[0]);
                }
                return a.localeCompare(b);
            });
        });

        return map;
    }, [rows, viewType]);



    const firstQuarterColumn =
        firstQuarter && viewType === "Quarter"
            ? {
                // title: `Q4 / ${getFYShortLabel(firstQuarter.split("-")[1])}`,
                title: `Q4/${getFYShortLabel(Number(firstQuarter.split("-")[1]))}`,
                dataIndex: firstQuarter,
                align: "center",
                width: 140,
                render: (val) => formatNumber(val),
                onHeaderCell: () => ({ style: headerStyle }),
            }
            : null;

    /* ---------- COLUMNS ---------- */
    //  REPLACE ONLY THIS columns useMemo (your existing one)
    const keys = [
        ...new Set(
            rows.flatMap(row => Object.keys(row))
        ),
    ];
    const yearColumns = keys
        .filter(key => /^\d{4}-\d{4}$/.test(key))
        .sort();

    const quarterColumns = keys
        .filter(key => /^Q[1-4]-\d{4}$/.test(key))
        .sort((a, b) => {
            const [qA, yA] = a.split("-");
            const [qB, yB] = b.split("-");

            if (yA !== yB) {
                return Number(yA) - Number(yB);
            }

            return Number(qA.replace("Q", "")) -
                Number(qB.replace("Q", ""));
        });

    const monthOrder = {
        Apr: 1,
        May: 2,
        Jun: 3,
        Jul: 4,
        Aug: 5,
        Sep: 6,
        Oct: 7,
        Nov: 8,
        Dec: 9,
        Jan: 10,
        Feb: 11,
        Mar: 12,
    };

    const monthColumns = keys
        .filter(key =>
            /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)-\d{4}$/.test(key)
        )
        .sort((a, b) => {
            const [monthA, yearA] = a.split("-");
            const [monthB, yearB] = b.split("-");

            const fyA =
                ["Jan", "Feb", "Mar"].includes(monthA)
                    ? Number(yearA) - 1
                    : Number(yearA);

            const fyB =
                ["Jan", "Feb", "Mar"].includes(monthB)
                    ? Number(yearB) - 1
                    : Number(yearB);

            if (fyA !== fyB) {
                return fyA - fyB;
            }

            return monthOrder[monthA] - monthOrder[monthB];
        });

    const periodColumns =
        viewType === "Year"
            ? yearColumns
            : viewType === "Quarter"
                ? quarterColumns
                : monthColumns;

    const columns = useMemo(() => {
        const cols = [
            {
                title: "Business Sectors",
                dataIndex: "label",
                fixed: "left",
                width: 460,
                className: "orders-column",
                render: (text, record) => {
                    if (record.isGrandTotal) {
                        return (
                            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <button
                                    type="button"
                                    aria-label={isAllExpanded ? "Collapse All" : "Expand All"}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleToggleExpandAll();
                                    }}
                                    className={`ant-table-row-expand-icon ant-table-row-expand-icon-${isAllExpanded ? "expanded" : "collapsed"}`}
                                    style={{ flexShrink: 0 }}
                                />
                                <strong>{text}</strong>
                            </span>
                        );
                    }
                    return <strong>{text}</strong>;
                },
                onHeaderCell: () => ({ style: headerStyle }),
            },
            {
                title: "IO No.",
                dataIndex: "IO_NO",
                width: 140,
                align: "center",
                render: (val) => val || "-",
                onHeaderCell: () => ({ style: headerStyle }),
            },
            {
                title: "INDAS",
                dataIndex: "IS_INDAS",
                width: 100,
                align: "center",
                render: (val) => (val === true ? "Yes" : val === false ? "No" : "-"),
                onHeaderCell: () => ({ style: headerStyle }),
            },
        ];

        if (parameter === "REVENUE") {
            const dynamicBalanceTitle = systemDate?.STARTDATE
                ? `Order as on ${systemDate.STARTDATE}`
                : getBalanceTitle();

            const dynamicBalanceRevenueColumn = {
                ...balanceRevenueColumn,
                title: dynamicBalanceTitle,
            };

            // 1. Order as on (system date)
            cols.push(dynamicBalanceRevenueColumn);
        }

        const groups = Object.keys(groupedKeys).sort(fySort);

        groups.forEach((year) => {
            let keys = groupedKeys[year] || [];

            if (viewType === "Quarter" && firstQuarter) {
                keys = keys.filter((k) => k !== firstQuarter);
            }

            if (viewType === "Year") {
                keys.forEach((k) => {
                    cols.push({
                        title: k,
                        dataIndex: k,
                        align: "center",
                        width: 140,
                        render: (val) => formatNumber(val),
                        onHeaderCell: () => ({ style: headerStyle }),
                    });
                });
                return;
            }

            cols.push({
                title: headerFYLabel(year),
                align: "center",
                onHeaderCell: () => ({ style: headerStyle }),
                children: [
                    ...keys.map((k, i) => ({
                        title: viewType === "Quarter" ? getQuarterLabel(k) : k,
                        dataIndex: k,
                        key: `${year}_${k}_${i}`,
                        align: "center",
                        width: 140,
                        render: (val) => formatNumber(val),
                    })),
                    buildYearTotalColumn(year),
                ],
            });
        });

        cols.push({
            title: "Overall Total",
            dataIndex: "total",
            fixed: "right",
            width: 120,
            align: "center",
            className: "total-column",
            render: (val) => formatNumber(val),
            onHeaderCell: () => ({ style: headerStyle }),
        });

        return cols;
    }, [groupedKeys, viewType, parameter, firstQuarter, systemDate, isAllExpanded, handleToggleExpandAll]);

    /* ===================== RENDER ===================== */

    return (
        <div className="p-5">
            <div className="flex items-center gap-3">
                <div className="flex gap-3 items-center">
                    <img src={ActiveProject} alt="region" />
                    <div>
                        <div className="font-inter font-semibold text-[24px] text-[#101828]">
                            {isClearParam === "1"
                                ? "Clear Other Projects - Execution Plan"
                                : isClearParam === "0"
                                    ? "Hold Other Projects - Execution Plan"
                                    : "Other Projects - Execution Plan"}
                        </div>
                        <div className="text-gray-500 text-sm">
                            * All figures are in Rupees (in Crores)
                        </div>
                    </div>
                </div>


            </div>

            <div className="mt-6 bg-white shadow-lg rounded-xl p-4">
                <div className="flex justify-between mb-4">
                    <div>
                        {/* <div className="font-semibold">Performance Plan - Execution Plan</div> */}
                        {/* <div className="text-sm text-gray-500">
              * All figures are in Rupees (in lakhs)
            </div> */}
                    </div>

                    <div className="flex gap-3">
                        {!restrictedUser && (
                            <CustomSelectWithFilter
                                data={[
                                    { label: "BHEL Overall", value: "BHEL" },
                                    ...(allUnitsList || []).map((u) => ({
                                        label: u.UNIT_DISPLAY_NAME,
                                        value: u.MNF_UNIT,
                                    })),
                                ]}
                                value={unit}
                                placeholder="BHEL Overall"
                                onChange={setUnit}
                                className="w-[14rem]"
                            />
                        )}

                        <Select
                            value={viewType}
                            onChange={setViewType}
                            style={{ width: 160 }}
                        >
                            <Option value="Year">Year Wise</Option>
                            <Option value="Quarter">Quarter Wise</Option>
                            <Option value="Month">Month Wise</Option>
                        </Select>

                        <CustomSelectWithFilter
                            data={[
                                { label: "Revenue", value: "REVENUE" },
                                { label: "Project cost", value: "PROJECT_COST" },
                                { label: "Profit/Loss", value: "PROFIT_LOSS" },
                                { label: "Apportioned cost", value: "APPORTIONED_COST" },
                                { label: "Operational income", value: "OPERATIONAL_INCOME" },
                                { label: "Project profit", value: "PROJECT_PROFIT" },
                                { label: "Net billing", value: "NET_BILLING" },
                                { label: "Cash inflow", value: "CASH_INFLOW" },
                                { label: "Cash outflow", value: "CASH_OUTFLOW" },
                                { label: "Net cash flow", value: "NET_CASH_FLOW" }
                            ]}
                            value={parameter}
                            onChange={setParameter}
                            placeholder="PROJECT_COST"
                            className="w-[14rem]"
                        />
                    </div>
                </div>

                <Table
                    bordered
                    loading={loading}
                    pagination={false}
                    scroll={{ x: "max-content", y: 800 }}
                    locale={{ emptyText: `No data found for ${selectedUnitLabel}` }}
                    className="execution-plan-table other-projects-table"
                    expandable={{
                        // Original predicate checked flags (isSubtotal / isBusinessSector /
                        // isUnit) that this file's rows never actually set — every row was
                        // silently non-expandable. Sector rows are the only ones with a
                        // `children` array, so that's the real signal to use.
                        // rowExpandable: (row) => row.isSubtotal || row.isBusinessSector || (row.isUnit && row.children && row.children.length > 0),
                        rowExpandable: (row) => Array.isArray(row.children) && row.children.length > 0,
                        expandedRowKeys,
                        onExpandedRowsChange: (keys) => setExpandedRowKeys(keys),
                    }}
                    rowClassName={(row) => {
                        if (row.isGrandTotal) {
                            return "grand-total-row";
                        }

                        if (row.isSubtotal) {
                            return "subtotal-row";
                        }

                        if (row.isBusinessSector) {
                            return "business-sector-row";
                        }

                        if (row.isUnit) {
                            return "unit-row";
                        }

                        return "project-row";
                    }}
                    columns={columns}
                    dataSource={tableData}
                    rowKey="key"
                />
            </div>
        </div>
    );
};

export default ExecutionPlan;
